import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import api from '../lib/api';

const ScoreBar = ({ label, score, color }) => (
  <div className="space-y-1">
    <div className="flex justify-between text-xs">
      <span className="text-gray-600">{label}</span>
      <span className="font-semibold" style={{ color }}>{score}/100</span>
    </div>
    <Progress value={score} className="h-2" />
  </div>
);

const KpiCard = ({ label, value, sub, color, onClick, clickable }) => (
  <div
    className={`bg-white p-3 rounded-lg border text-center flex flex-col justify-between min-h-[90px] ${clickable ? 'cursor-pointer hover:shadow-md hover:border-indigo-300 transition-all' : ''}`}
    onClick={clickable ? onClick : undefined}
    data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}
  >
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-lg font-bold" style={{ color: color || '#1e293b' }}>{value}</p>
    <p className="text-[10px] text-gray-400 min-h-[14px]">{sub || '\u00A0'}</p>
    {clickable && <p className="text-[9px] text-indigo-500 mt-0.5">Click for details</p>}
  </div>
);

const getRiskColor = (level) => {
  if (level === 'low') return 'bg-green-100 text-green-700';
  if (level === 'medium') return 'bg-amber-100 text-amber-700';
  return 'bg-red-100 text-red-700';
};

const VendorAnalytics = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [analytics, setAnalytics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzingVendor, setAnalyzingVendor] = useState(null);
  const [aiResult, setAiResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [enhancedData, setEnhancedData] = useState(null);
  const [detailView, setDetailView] = useState(null); // 'price-trends'
  const [riskAlerts, setRiskAlerts] = useState([]);
  const [showRiskAlerts, setShowRiskAlerts] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [compareIds, setCompareIds] = useState([]);
  const [compareData, setCompareData] = useState(null);
  const [reorderData, setReorderData] = useState(null);
  const [reorderLoading, setReorderLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('vendors'); // 'vendors' | 'risk-alerts'
  const [showProfit, setShowProfit] = useState(false);

  // Filters
  const [filterCity, setFilterCity] = useState('');
  const [filterState, setFilterState] = useState('');
  const [filterArea, setFilterArea] = useState('');
  const [filterMinPOs, setFilterMinPOs] = useState('');
  const [filterSearch, setFilterSearch] = useState('');

  useEffect(() => { loadAnalytics(); loadRiskAlerts(); }, []);

  // Auto-reopen AI Analysis when returning via back button (URL has ?vendor=...)
  useEffect(() => {
    const vendorParam = searchParams.get('vendor');
    if (vendorParam && analytics.length > 0 && !analyzingVendor && !aiLoading) {
      const vendor = analytics.find(v => v.vendor_id === vendorParam);
      if (vendor) {
        runAIAnalysis(vendor.vendor_id, vendor.vendor_name);
      }
    }
  }, [analytics, searchParams]); // eslint-disable-line

  const loadAnalytics = async () => {
    try {
      const res = await api.get('/vendor-analytics');
      setAnalytics(res.data);
    } catch { toast.error('Failed to load analytics'); }
    finally { setLoading(false); }
  };

  const loadRiskAlerts = async () => {
    try {
      const res = await api.get('/vendor-analytics/risk-alerts');
      setRiskAlerts(res.data);
    } catch { /* silent */ }
  };

  // Filter values
  const cities = useMemo(() => [...new Set(analytics.map(v => v.city).filter(Boolean))].sort(), [analytics]);
  const states = useMemo(() => [...new Set(analytics.map(v => v.state).filter(Boolean))].sort(), [analytics]);
  const areas = useMemo(() => [...new Set(analytics.map(v => v.area).filter(Boolean))].sort(), [analytics]);

  const filtered = useMemo(() => {
    return analytics.filter(v => {
      if (filterCity && v.city !== filterCity) return false;
      if (filterState && v.state !== filterState) return false;
      if (filterArea && v.area !== filterArea) return false;
      if (filterMinPOs && v.total_orders < parseInt(filterMinPOs)) return false;
      if (filterSearch && !v.vendor_name.toLowerCase().includes(filterSearch.toLowerCase())) return false;
      return true;
    });
  }, [analytics, filterCity, filterState, filterArea, filterMinPOs, filterSearch]);

  const runAIAnalysis = async (vendorId, vendorName) => {
    setAnalyzingVendor(vendorId);
    setAiLoading(true);
    setAiResult(null);
    setEnhancedData(null);
    setDetailView(null);
    // Persist vendor in URL so back navigation restores AI panel
    setSearchParams({ vendor: vendorId }, { replace: true });
    try {
      const [aiRes, enhRes] = await Promise.all([
        api.get(`/vendor-analytics/${vendorId}/ai`),
        api.get(`/vendor-analytics/enhanced/${vendorId}`),
      ]);
      setAiResult(aiRes.data);
      setEnhancedData(enhRes.data);
      toast.success(`AI analysis complete for ${vendorName}`);
      setTimeout(() => {
        document.getElementById('ai-result-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (err) {
      toast.error('AI analysis failed.');
    }
    finally { setAiLoading(false); }
  };

  const runReorderSuggestion = async (vendorId) => {
    setReorderLoading(true);
    setReorderData(null);
    try {
      const res = await api.get(`/vendor-analytics/reorder-suggestions/${vendorId}`);
      setReorderData(res.data);
      toast.success('Reorder suggestions ready');
    } catch { toast.error('Failed to get reorder suggestions'); }
    finally { setReorderLoading(false); }
  };

  const runComparison = async () => {
    if (compareIds.length < 2) { toast.error('Select at least 2 vendors to compare'); return; }
    try {
      const res = await api.get(`/vendor-analytics/compare?vendor_ids=${compareIds.join(',')}`);
      setCompareData(res.data);
    } catch { toast.error('Comparison failed'); }
  };

  const toggleCompare = (vid) => {
    setCompareIds(prev => prev.includes(vid) ? prev.filter(id => id !== vid) : prev.length >= 3 ? prev : [...prev, vid]);
  };

  const clearFilters = () => {
    setFilterCity(''); setFilterState(''); setFilterArea(''); setFilterMinPOs(''); setFilterSearch('');
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;

  return (
    <div className="p-6 space-y-5 animate-fadeIn" data-testid="vendor-analytics-container">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }}>Vendor Analytics</h1>
          <p className="text-gray-600 mt-1">AI-powered vendor performance insights</p>
        </div>
        <div className="flex gap-2">
          {riskAlerts.length > 0 && (
            <Button variant="outline" size="sm" className="text-red-600 border-red-300 hover:bg-red-50 gap-1.5"
              onClick={() => setActiveTab(activeTab === 'risk-alerts' ? 'vendors' : 'risk-alerts')} data-testid="toggle-risk-alerts">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
              Risk Alerts ({riskAlerts.length})
            </Button>
          )}
          <Button variant={compareMode ? 'default' : 'outline'} size="sm" className={compareMode ? 'bg-indigo-600' : 'gap-1.5'}
            onClick={() => { setCompareMode(!compareMode); setCompareIds([]); setCompareData(null); }} data-testid="toggle-compare">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
            {compareMode ? `Compare (${compareIds.length}/3)` : 'Compare Vendors'}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-gray-200">
        <CardContent className="p-3">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[150px]">
              <Label className="text-xs text-gray-500">Search</Label>
              <Input placeholder="Vendor name..." value={filterSearch} onChange={e => setFilterSearch(e.target.value)} className="h-8 text-sm" data-testid="filter-search" />
            </div>
            <div className="min-w-[130px]">
              <Label className="text-xs text-gray-500">State</Label>
              <select value={filterState} onChange={e => setFilterState(e.target.value)} className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs" data-testid="filter-state">
                <option value="">All States</option>
                {states.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="min-w-[130px]">
              <Label className="text-xs text-gray-500">City</Label>
              <select value={filterCity} onChange={e => setFilterCity(e.target.value)} className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs" data-testid="filter-city">
                <option value="">All Cities</option>
                {cities.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            {areas.length > 0 && (
              <div className="min-w-[130px]">
                <Label className="text-xs text-gray-500">Area</Label>
                <select value={filterArea} onChange={e => setFilterArea(e.target.value)} className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs" data-testid="filter-area">
                  <option value="">All Areas</option>
                  {areas.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
            )}
            <div className="min-w-[100px]">
              <Label className="text-xs text-gray-500">Min POs</Label>
              <select value={filterMinPOs} onChange={e => setFilterMinPOs(e.target.value)} className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs" data-testid="filter-min-pos">
                <option value="">Any</option>
                <option value="1">1+ POs</option>
                <option value="5">5+ POs</option>
                <option value="10">10+ POs</option>
              </select>
            </div>
            <Button variant="ghost" size="sm" className="h-8 text-xs text-gray-500" onClick={clearFilters} data-testid="clear-filters">Clear</Button>
          </div>
        </CardContent>
      </Card>

      {/* Compare Bar */}
      {compareMode && compareIds.length >= 2 && (
        <div className="flex items-center gap-3 bg-indigo-50 p-3 rounded-lg border border-indigo-200">
          <span className="text-sm font-medium text-indigo-800">
            {compareIds.length} vendors selected
          </span>
          <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={runComparison} data-testid="run-compare">
            Compare Now
          </Button>
        </div>
      )}

      {/* Risk Alerts Tab */}
      {activeTab === 'risk-alerts' && (
        <Card className="border-red-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-red-800 text-lg">Vendor Risk Alerts</CardTitle>
            <CardDescription>Vendors flagged for poor delivery, quality, or delays</CardDescription>
          </CardHeader>
          <CardContent>
            {riskAlerts.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No risk alerts! All vendors performing well.</p>
            ) : (
              <div className="space-y-2">
                {riskAlerts.map(a => (
                  <div key={a.vendor_id} className="flex items-center justify-between p-3 rounded-lg border bg-white hover:bg-gray-50" data-testid={`risk-alert-${a.vendor_id}`}>
                    <div className="flex items-center gap-3">
                      <Badge className={getRiskColor(a.risk_level)}>{a.risk_level.toUpperCase()}</Badge>
                      <div>
                        <p className="font-semibold text-sm text-gray-900">{a.vendor_name}</p>
                        <p className="text-xs text-gray-500">{a.city} | {a.total_pos} POs</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-xs">
                      <span>Delivery: <strong>{a.delivery_rate}%</strong></span>
                      <span>Defects: <strong>{a.defect_pct}%</strong></span>
                      <span>Delays: <strong>{a.delay_pct}%</strong></span>
                      <div className="flex flex-col gap-0.5">
                        {a.flags.map((f, i) => <span key={i} className="text-red-600">{f}</span>)}
                      </div>
                    </div>
                    <Button size="sm" variant="outline" className="text-indigo-600" onClick={() => { setActiveTab('vendors'); runAIAnalysis(a.vendor_id, a.vendor_name); }}>
                      Analyze
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Comparison View */}
      {compareData && (
        <Card className="border-2 border-indigo-200" data-testid="compare-result">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-indigo-900">Vendor Comparison</CardTitle>
              <Button size="sm" variant="ghost" onClick={() => setCompareData(null)}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-indigo-50">
                    <th className="p-2 text-left text-xs font-semibold text-gray-600">Metric</th>
                    {compareData.map(v => <th key={v.vendor_id} className="p-2 text-center text-xs font-semibold text-indigo-800">{v.vendor_name}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: 'City', key: 'vendor_city' },
                    { label: 'Total POs', key: 'total_pos' },
                    { label: 'Qty Ordered', key: 'qty_ordered' },
                    { label: 'Qty Received', key: 'qty_received' },
                    { label: 'Qty Cancelled', key: 'qty_cancelled' },
                    { label: 'Delivery Rate', key: 'delivery_rate_pct', suffix: '%' },
                    { label: 'Current Profit (SE)', key: 'se_profit', prefix: '₹', color: true },
                    { label: 'Profit %', key: 'se_profit_pct', suffix: '%', color: true },
                    { label: 'Qty Sold (SE)', key: 'se_qty_sold' },
                    { label: 'Stock in Hand (Qty)', key: 'total_stock_qty' },
                    { label: 'Stock Value', key: 'total_stock_value', prefix: '₹' },
                    { label: 'Defect %', key: 'defect_pct', suffix: '%' },
                    { label: 'Delay %', key: 'delay_pct', suffix: '%' },
                  ].map(row => (
                    <tr key={row.key} className="border-b">
                      <td className="p-2 text-xs font-medium text-gray-700">{row.label}</td>
                      {compareData.map(v => {
                        const val = v[row.key];
                        const display = `${row.prefix || ''}${typeof val === 'number' ? val.toLocaleString('en-IN') : val || '-'}${row.suffix || ''}`;
                        const best = row.color && compareData.length > 1 ? Math.max(...compareData.map(c => c[row.key] || 0)) : null;
                        return (
                          <td key={v.vendor_id} className={`p-2 text-center text-xs ${row.color && val === best ? 'font-bold text-green-700' : ''}`}>
                            {display}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* AI Analysis Result */}
      {aiResult && enhancedData && (
        <Card className="border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-white" data-testid="ai-result-card" id="ai-result-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-indigo-900">AI Analysis: {aiResult.vendor_name}</CardTitle>
                <CardDescription>
                  {enhancedData.total_pos} orders | {enhancedData.vendor_city}, {enhancedData.vendor_state}
                  {aiResult.generated_at && <span className="ml-2 text-xs">| {new Date(aiResult.generated_at).toLocaleString()}</span>}
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {aiResult.risk_level && <Badge className={getRiskColor(aiResult.risk_level)}>{aiResult.risk_level.toUpperCase()} RISK</Badge>}
                <Button size="sm" variant="ghost" className="h-7 px-2 text-gray-400" onClick={() => { setAiResult(null); setEnhancedData(null); setDetailView(null); setReorderData(null); }}>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-700 bg-white p-3 rounded-lg border">{aiResult.summary}</p>

            {/* Real KPI Figures */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
              {/* Row 1 */}
              <KpiCard label="Qty Ordered" value={enhancedData.qty_ordered.toLocaleString('en-IN')}
                sub={`Value: ₹${(enhancedData.total_ordered_value || 0).toLocaleString('en-IN')}`}
                color="#1e40af" />
              <KpiCard label="Qty Received" value={enhancedData.qty_received.toLocaleString('en-IN')}
                sub={`Value: ₹${(enhancedData.total_cost || 0).toLocaleString('en-IN')}`}
                color="#059669" />
              <KpiCard label="Qty Cancelled" value={enhancedData.qty_cancelled.toLocaleString('en-IN')}
                sub={`Value: ₹${(enhancedData.cancelled_value || 0).toLocaleString('en-IN')}`}
                color="#dc2626" />
              <KpiCard label="Qty Pending" value={enhancedData.qty_pending.toLocaleString('en-IN')}
                sub={`Value: ₹${(enhancedData.pending_value || 0).toLocaleString('en-IN')}`}
                color="#d97706" />
              <KpiCard label="Delivery Rate" value={`${enhancedData.delivery_rate_pct}%`} color={enhancedData.delivery_rate_pct >= 80 ? '#059669' : '#dc2626'} />
              {/* Row 2 */}
              <KpiCard label="Qty Sold" value={(enhancedData.se_qty_sold || 0).toLocaleString('en-IN')}
                sub={`Revenue: ₹${(enhancedData.se_revenue || 0).toLocaleString('en-IN')}`}
                color="#7c3aed" />
              <KpiCard label="Sales Return" value={(enhancedData.qty_sales_returned || 0).toLocaleString('en-IN')}
                sub={`Value: ₹${(enhancedData.sales_return_value || 0).toLocaleString('en-IN')}`}
                color="#0891b2" />
              <KpiCard label="Purchase Return" value={(enhancedData.qty_purchase_returned || 0).toLocaleString('en-IN')}
                sub={`Value: ₹${(enhancedData.purchase_return_value || 0).toLocaleString('en-IN')}`}
                color="#ea580c" />
              <KpiCard label="Stock in Hand" value={`${enhancedData.total_stock_qty} pcs`}
                sub={`₹${enhancedData.total_stock_value.toLocaleString('en-IN')}`}
                color="#7c3aed" clickable onClick={() => navigate(`/vendor-stock/${analyzingVendor}`)} />
              {showProfit ? (
                <KpiCard label="Current Profit (SE)" value={`₹${(enhancedData.se_profit || 0).toLocaleString('en-IN')}`}
                  sub={`${enhancedData.se_profit_pct || 0}%`}
                  color={(enhancedData.se_profit || 0) >= 0 ? '#059669' : '#dc2626'} />
              ) : (
                <div className="bg-white p-3 rounded-lg border text-center flex flex-col justify-between min-h-[90px] cursor-pointer hover:shadow-md hover:border-indigo-300 transition-all"
                  onClick={() => setShowProfit(true)} data-testid="kpi-profit-hidden">
                  <p className="text-xs text-gray-500">Current Profit (SE)</p>
                  <p className="text-lg font-bold text-gray-400">* * * *</p>
                  <p className="text-[10px] text-indigo-500">Tap to reveal</p>
                </div>
              )}
            </div>
            {showProfit && (
              <div className="flex justify-end">
                <button className="text-xs text-gray-400 hover:text-gray-600 underline" onClick={() => setShowProfit(false)} data-testid="hide-profit-btn">
                  Hide profit
                </button>
              </div>
            )}

            {/* Scores */}
            {aiResult.scores && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <ScoreBar label="Delivery" score={aiResult.scores.delivery || 0} color="#059669" />
                <ScoreBar label="Profitability" score={aiResult.scores.profitability || 0} color="#2563eb" />
                <ScoreBar label="Quality" score={aiResult.scores.quality || 0} color="#7c3aed" />
                <ScoreBar label="Overall" score={aiResult.scores.overall || 0} color="#dc2626" />
              </div>
            )}

            {/* Insights & Recommendations */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {aiResult.insights?.length > 0 && (
                <div className="bg-white p-3 rounded-lg border">
                  <h4 className="font-semibold text-sm text-gray-900 mb-2">Key Insights</h4>
                  <ul className="space-y-1">
                    {aiResult.insights.map((ins, i) => (
                      <li key={i} className="text-xs text-gray-600 flex items-start gap-1.5">
                        <svg className="w-3 h-3 text-indigo-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                        {ins}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {aiResult.recommendations?.length > 0 && (
                <div className="bg-white p-3 rounded-lg border">
                  <h4 className="font-semibold text-sm text-gray-900 mb-2">Recommendations</h4>
                  <ul className="space-y-1">
                    {aiResult.recommendations.map((rec, i) => (
                      <li key={i} className="text-xs text-gray-600 flex items-start gap-1.5">
                        <svg className="w-3 h-3 text-amber-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" /></svg>
                        {rec}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {aiResult.order_frequency_assessment && (
              <p className="text-xs text-gray-500 italic">Order Frequency: {aiResult.order_frequency_assessment}</p>
            )}

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2 pt-2 border-t">
              <Button size="sm" variant="outline" className="text-xs gap-1" onClick={() => navigate(`/vendor-stock/${analyzingVendor}`)} data-testid="view-stock-detail">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
                Stock in Hand
              </Button>
              <Button size="sm" variant="outline" className="text-xs gap-1" onClick={() => setDetailView('price-trends')} data-testid="view-price-trends">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
                Price Trends
              </Button>
              <Button size="sm" variant="outline" className="text-xs gap-1 text-green-700 border-green-300 hover:bg-green-50"
                onClick={() => runReorderSuggestion(analyzingVendor)} disabled={reorderLoading} data-testid="reorder-suggestion">
                {reorderLoading ? <><div className="animate-spin h-3 w-3 border-b-2 border-green-600 rounded-full mr-1"></div>Analyzing...</> :
                  <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>AI Reorder Suggestions</>}
              </Button>
            </div>

            {/* Reorder Suggestions */}
            {reorderData && (
              <div className="bg-green-50 p-4 rounded-lg border border-green-200 space-y-3" data-testid="reorder-result">
                <h4 className="font-bold text-sm text-green-800">AI Reorder Suggestions</h4>
                {reorderData.ai_summary && <p className="text-xs text-gray-700">{reorderData.ai_summary}</p>}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {reorderData.urgent_reorders?.length > 0 && (
                    <div className="bg-white p-3 rounded border">
                      <h5 className="font-semibold text-xs text-red-700 mb-1">Urgent Reorders</h5>
                      {reorderData.urgent_reorders.map((r, i) => <p key={i} className="text-[11px] text-gray-600">• {r}</p>)}
                    </div>
                  )}
                  {reorderData.watch_list?.length > 0 && (
                    <div className="bg-white p-3 rounded border">
                      <h5 className="font-semibold text-xs text-amber-700 mb-1">Watch List</h5>
                      {reorderData.watch_list.map((r, i) => <p key={i} className="text-[11px] text-gray-600">• {r}</p>)}
                    </div>
                  )}
                  {reorderData.overstock_warning?.length > 0 && (
                    <div className="bg-white p-3 rounded border">
                      <h5 className="font-semibold text-xs text-blue-700 mb-1">Overstock Warning</h5>
                      {reorderData.overstock_warning.map((r, i) => <p key={i} className="text-[11px] text-gray-600">• {r}</p>)}
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Vendor Cards Grid */}
      {activeTab === 'vendors' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(v => (
            <Card key={v.vendor_id} className={`hover:shadow-lg transition-all border-l-4 ${compareMode && compareIds.includes(v.vendor_id) ? 'border-l-indigo-600 ring-2 ring-indigo-300' : 'border-l-indigo-400'}`} data-testid={`vendor-card-${v.vendor_id}`}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-gray-900 truncate">{v.vendor_name}</h3>
                    <p className="text-xs text-gray-500">{[v.area, v.city, v.state].filter(Boolean).join(', ')}</p>
                  </div>
                  <div className="flex items-center gap-1.5 ml-2">
                    <Badge className="bg-indigo-100 text-indigo-700">{v.total_orders} POs</Badge>
                    {compareMode && (
                      <input type="checkbox" checked={compareIds.includes(v.vendor_id)}
                        onChange={() => toggleCompare(v.vendor_id)}
                        className="w-4 h-4 accent-indigo-600" data-testid={`compare-check-${v.vendor_id}`} />
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-gray-50 p-2 rounded">
                    <span className="text-gray-500">Total Value</span>
                    <p className="font-bold text-gray-900">₹{(v.total_amount || 0).toLocaleString('en-IN')}</p>
                  </div>
                  <div className="bg-gray-50 p-2 rounded">
                    <span className="text-gray-500">Total Pieces</span>
                    <p className="font-bold text-gray-900">{(v.total_qty || 0).toLocaleString('en-IN')}</p>
                  </div>
                </div>
                <Button size="sm" variant="outline" className="w-full text-indigo-600 border-indigo-300 hover:bg-indigo-50"
                  onClick={() => runAIAnalysis(v.vendor_id, v.vendor_name)}
                  disabled={aiLoading && analyzingVendor === v.vendor_id}
                  data-testid={`ai-analyze-${v.vendor_id}`}>
                  {aiLoading && analyzingVendor === v.vendor_id ? (
                    <><div className="animate-spin h-3 w-3 border-b-2 border-indigo-600 rounded-full mr-2"></div>Analyzing...</>
                  ) : (
                    <><svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>AI Analysis</>
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full text-center py-12 text-gray-500">
              <p className="text-lg font-medium">No vendors match filters</p>
              <Button variant="link" className="text-indigo-600" onClick={clearFilters}>Clear filters</Button>
            </div>
          )}
        </div>
      )}

      {/* Detail Dialogs */}
      <Dialog open={detailView === 'price-trends'} onOpenChange={v => { if (!v) setDetailView(null); }}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto z-50">
          <DialogHeader>
            <DialogTitle>Price Trend Analysis</DialogTitle>
            <DialogDescription>{enhancedData?.vendor_name} — How pricing changed across POs</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {enhancedData?.price_trends?.map((pt, i) => (
              <div key={i} className="bg-gray-50 p-3 rounded-lg border">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-sm">{pt.design_code} — {pt.size} / {pt.color}</span>
                  <Badge className={pt.change_pct > 0 ? 'bg-red-100 text-red-700' : pt.change_pct < 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
                    {pt.change_pct > 0 ? '+' : ''}{pt.change_pct}%
                  </Badge>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {pt.history.map((h, j) => (
                    <div key={j} className="bg-white px-2 py-1 rounded text-xs border">
                      <span className="text-gray-500">{h.date}</span>
                      <span className="ml-1.5 font-semibold">₹{h.cost}</span>
                      <span className="ml-1 text-gray-400 text-[10px]">{h.po_number}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {(!enhancedData?.price_trends || enhancedData.price_trends.length === 0) && (
              <p className="text-sm text-gray-500 text-center py-6">No price history with multiple data points available.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VendorAnalytics;
